import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';

export default function JoinPage() {
  const { pin: urlPin } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const [step, setStep] = useState(urlPin ? 'loading' : 'pin');
  const [pin, setPin] = useState(urlPin || '');
  const [quizInfo, setQuizInfo] = useState(null);
  const [form, setForm] = useState({ firstName: '', lastName: '', birthDate: '', language: i18n.language || 'de', team: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Auto-lookup when PIN comes from URL (QR code)
  useEffect(() => {
    if (!urlPin) return;
    api.get(`/auth/quiz/${urlPin.trim()}`)
      .then(({ data }) => { setQuizInfo(data.quiz); setStep('form'); })
      .catch(err => { setError(err.response?.data?.error || 'PIN nicht gefunden oder abgelaufen'); setStep('pin'); });
  }, [urlPin]);

  const handle = (e) => {
    setForm(p => ({ ...p, [e.target.name]: e.target.value }));
    if (e.target.name === 'language') i18n.changeLanguage(e.target.value);
  };

  // Step 1: PIN lookup
  const lookupPin = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const { data } = await api.get(`/auth/quiz/${pin.trim()}`);
      setQuizInfo(data.quiz);
      setStep('form');
    } catch (err) {
      setError(err.response?.data?.error || 'PIN nicht gefunden oder abgelaufen');
    } finally { setLoading(false); }
  };

  // Step 2: Join as competitor
  const submitJoin = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const body = {
        pin,
        firstName: form.firstName,
        lastName: form.lastName,
        language: form.language || 'de',
        ...(form.birthDate ? { birthDate: form.birthDate } : {}),
      };
      const { data } = await api.post('/auth/competitor/join', body);
      if (data.token) sessionStorage.setItem('ilmify_pt', data.token); // Teilnehmer-Token für Socket.IO
      const participant = { ...data.participant, team: form.team || null };
      localStorage.setItem('zq_participant', JSON.stringify(participant));
      localStorage.setItem('zq_lang', form.language);
      navigate(`/lobby/${data.quiz.sessionId}`, {
        state: { quiz: { ...data.quiz, ...quizInfo }, participant },
      });
    } catch (err) {
      setError(err.response?.data?.error || t('auth.loginError'));
    } finally { setLoading(false); }
  };

  // Spectator: go directly to spectator page using session ID from quizInfo
  const joinAsSpectator = () => {
    if (!quizInfo?.sessionId) { setError('Keine aktive Session gefunden'); return; }
    navigate(`/spectate/${quizInfo.sessionId}`);
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ width: '100%', maxWidth: 420 }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <h1 style={{ fontFamily: 'Playfair Display', fontSize: 32, color: 'var(--primary-dk)' }}>Ilmi<span style={{ color: 'var(--accent)' }}>fy</span></h1>
        </div>

        {/* Loading state (auto-lookup via URL pin) */}
        {step === 'loading' && (
          <div className="card" style={{ textAlign: 'center', padding: '40px 20px' }}>
            <div className="spinner" style={{ margin: '0 auto 16px' }} />
            <p style={{ color: 'var(--text3)' }}>Quiz wird geladen...</p>
          </div>
        )}

        {/* Step 1: PIN */}
        {step === 'pin' && (
          <div className="card">
            <h2 style={{ fontSize: 20, marginBottom: 20, textAlign: 'center' }}>{t('quiz.join')}</h2>
            {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}
            <form onSubmit={lookupPin}>
              <div className="form-group">
                <label className="form-label">{t('quiz.pin')}</label>
                <input
                  className="form-input"
                  style={{ fontSize: 28, textAlign: 'center', letterSpacing: 8, fontWeight: 700 }}
                  value={pin}
                  onChange={e => setPin(e.target.value)}
                  maxLength={6}
                  pattern="[0-9]{4,10}"
                  required
                  placeholder="000000"
                  autoFocus
                />
              </div>
              <button className="btn btn-primary btn-lg w-full mt-4" type="submit" disabled={loading}>
                {loading ? t('common.loading') : 'Quiz suchen →'}
              </button>
            </form>
          </div>
        )}

        {/* Step 2: Name + options */}
        {step === 'form' && quizInfo && (
          <div className="card">
            {/* Quiz info header */}
            <div style={{ background: 'var(--primary-lt)', borderRadius: 10, padding: '12px 16px', marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontWeight: 700, color: 'var(--primary)', fontSize: 15 }}>{quizInfo.title}</div>
                <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>PIN: {pin}</div>
              </div>
              <button type="button" onClick={() => { setStep('pin'); setQuizInfo(null); setError(''); }}
                style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', fontSize: 13 }}>
                ✕ Ändern
              </button>
            </div>

            {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}

            {/* Language */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 20, justifyContent: 'center' }}>
              {['de', 'al'].map(lang => (
                <button key={lang} type="button"
                  onClick={() => handle({ target: { name: 'language', value: lang } })}
                  className={`btn btn-sm ${form.language === lang ? 'btn-primary' : 'btn-ghost'}`}>
                  {lang === 'de' ? '🇩🇪 Deutsch' : '🇦🇱 Shqip'}
                </button>
              ))}
            </div>

            <form onSubmit={submitJoin}>
              <div className="grid-2">
                <div className="form-group">
                  <label className="form-label">{t('quiz.firstName')}</label>
                  <input className="form-input" name="firstName" value={form.firstName} onChange={handle} required />
                </div>
                <div className="form-group">
                  <label className="form-label">{t('quiz.lastName')}</label>
                  <input className="form-input" name="lastName" value={form.lastName} onChange={handle} required />
                </div>
              </div>

              {/* Birth date — required for family mode, optional otherwise */}
              <div className="form-group">
                <label className="form-label">
                  {t('quiz.birthDate')}
                  {quizInfo.enableFamilyMode && <span style={{ color: 'var(--error)', marginLeft: 4 }}>*</span>}
                  {!quizInfo.enableFamilyMode && <span style={{ color: 'var(--text3)', fontSize: 11, marginLeft: 6 }}>(optional)</span>}
                </label>
                <input className="form-input" type="date" name="birthDate" value={form.birthDate} onChange={handle}
                  required={quizInfo.enableFamilyMode} />
                {quizInfo.enableFamilyMode && quizInfo.ageGroups?.length > 0 && (
                  <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 6 }}>
                    Altersgruppen: {quizInfo.ageGroups.map(g => `${g.label} (${g.min}–${g.max} J.)`).join(', ')}
                  </div>
                )}
              </div>

              {/* Duel team selector */}
              {quizInfo.is_duel && (
                <div className="form-group">
                  <label className="form-label">
                    Team wählen <span style={{ color: 'var(--error)', marginLeft: 2 }}>*</span>
                  </label>
                  <div style={{ display: 'flex', gap: 10 }}>
                    {[
                      { value: quizInfo.mosqueName, name: quizInfo.mosqueName, logo: quizInfo.mosque_logo },
                      { value: quizInfo.duel_mosque_name, name: quizInfo.duel_mosque_name, logo: quizInfo.duel_mosque_logo },
                    ].map(opt => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setForm(p => ({ ...p, team: opt.value }))}
                        style={{
                          flex: 1, padding: '14px 10px', borderRadius: 10, cursor: 'pointer',
                          border: `2px solid ${form.team === opt.value ? 'var(--primary)' : 'var(--border)'}`,
                          background: form.team === opt.value ? 'var(--primary-lt)' : 'var(--surface)',
                          color: form.team === opt.value ? 'var(--primary)' : 'var(--text)',
                          fontWeight: form.team === opt.value ? 700 : 500,
                          fontSize: 13, textAlign: 'center',
                          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
                        }}
                      >
                        {opt.logo
                          ? <img src={opt.logo} alt={opt.name} style={{ width: 48, height: 48, objectFit: 'contain', borderRadius: 8, background: '#fff', padding: 3, border: '1px solid var(--border)' }} />
                          : <div style={{ width: 48, height: 48, borderRadius: 8, background: form.team === opt.value ? 'var(--primary)' : 'var(--border)', color: form.team === opt.value ? '#fff' : 'var(--text3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 20 }}>
                              {opt.name?.[0] || '?'}
                            </div>
                        }
                        <span>{opt.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <button
                className="btn btn-primary btn-lg w-full mt-4"
                type="submit"
                disabled={loading || (quizInfo.is_duel && !form.team)}
              >
                {loading ? t('common.loading') : t('quiz.startQuiz')}
              </button>
            </form>

            {/* Spectator option */}
            {quizInfo.enableSpectators && quizInfo.sessionId && (
              <div style={{ marginTop: 16, textAlign: 'center', borderTop: '1px solid var(--border)', paddingTop: 16 }}>
                <button type="button" onClick={joinAsSpectator}
                  style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', fontSize: 13, textDecoration: 'underline' }}>
                  Nur zuschauen (Zuschauer-Modus)
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
